import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Col, Container, Row } from "reactstrap";
import facebookFooter from "../../../assets/preview/facebook-buttons-footer.png";
import instagramFooter1 from "../../../assets/preview/instagram-buttons-footer.png";
import instagramFooter2 from "../../../assets/preview/instagram-preview-footer-2.png";
import instagramHeader from "../../../assets/preview/instagram-preview-header.png";
import linkedInFooter from "../../../assets/preview/linkedin-buttons-footer.png";
import fakeText from "../../../assets/preview/preview-fake-text.png";
import fakeTextBig from "../../../assets/preview/preview-fake-text-big.png";
import fakeTextHeader from "../../../assets/preview/preview-fake-text-header.png";
import twitterFooter from "../../../assets/preview/twitter-preview-footer.png";
import twitterHeader from "../../../assets/preview/twitter-preview-header.png";
import "./StaticSocialPostPreview.scss";

export type StaticSocialPreviewPlatform =
  | "instagram"
  | "facebook"
  | "linkedin"
  | "twitter"
  | "tiktok";

type Props = {
  platform: StaticSocialPreviewPlatform;
  text: string;
  imageUrl?: string | null;
  displayName: string;
  avatarUrl?: string | null;
  onImageClick?: () => void;
  /** Formato de la pieza generada (afecta el frame del preview). */
  imageAspect?: "1:1" | "2:3";
  /** Oculta el caption largo (útil en modal donde el texto ya está a la derecha). */
  compact?: boolean;
};

function Avatar({
  url,
  name,
  className,
}: {
  url?: string | null;
  name: string;
  className: string;
}) {
  if (url) {
    return <img className={className} src={url} alt="" />;
  }
  return (
    <div className={`${className} staticSocialPreview-avatarPlaceholder`} aria-hidden>
      {(name || "W").slice(0, 1).toUpperCase()}
    </div>
  );
}

function Media({
  imageUrl,
  className,
  emptyLabel = "El diseño aparecerá aquí",
  onImageClick,
  imageAspect = "1:1",
}: {
  imageUrl?: string | null;
  className: string;
  emptyLabel?: string;
  onImageClick?: () => void;
  imageAspect?: "1:1" | "2:3";
}) {
  const aspectClass =
    imageAspect === "2:3"
      ? "staticSocialPreview-media--portrait"
      : "staticSocialPreview-media--square";
  if (imageUrl) {
    return (
      <button
        type="button"
        className={`${className} staticSocialPreview-mediaBtn ${aspectClass}`}
        onClick={onImageClick}
        disabled={!onImageClick}
        title={onImageClick ? "Abrir editor de imagen" : undefined}
      >
        <img src={imageUrl} alt="Vista previa de la publicación" />
      </button>
    );
  }
  return (
    <div
      className={`${className} staticSocialPreview-mediaEmpty ${aspectClass}`}
    >
      {emptyLabel}
    </div>
  );
}

function Caption({
  text,
  className,
  emptySrc,
}: {
  text: string;
  className?: string;
  emptySrc: string;
}) {
  if (text?.trim()) {
    return (
      <div className={`line-break-anywhere pre-wrap ${className || ""}`}>
        {text}
      </div>
    );
  }
  return <img src={emptySrc} alt="" className="staticSocialPreview-fakeText" />;
}

function InstagramStaticPreview({
  text,
  imageUrl,
  displayName,
  avatarUrl,
  onImageClick,
  imageAspect,
  compact,
}: Omit<Props, "platform">) {
  return (
    <div
      className={`instagramPreview-container neu-out p-2${
        compact ? " staticSocialPreview--compact" : ""
      }`}
    >
      <Container fluid>
        <Row className="instagramPreview-header">
          <Col xs="12" className="p-0">
            <img className="w-100" src={instagramHeader} alt="" />
          </Col>
        </Row>
        <Row className="instagramPreview-header">
          <Col xs="6" className="d-flex align-items-center p-0 px-2">
            <Avatar
              url={avatarUrl}
              name={displayName}
              className="instagramPreview-header-avatar"
            />
            <div>
              <div>{displayName}</div>
              <div>
                <img src={fakeTextHeader} alt="" />
              </div>
            </div>
          </Col>
          <Col
            xs="6"
            className="align-content-center col-6 d-flex justify-content-end"
          >
            <FontAwesomeIcon icon={["fas", "ellipsis-h"]} size="1x" />
          </Col>
        </Row>
        <Row>
          <Col xs="12" className="p-0">
            <Media
              imageUrl={imageUrl}
              className="instagramPreview-image"
              onImageClick={onImageClick}
              imageAspect={imageAspect}
            />
          </Col>
        </Row>
        <Row>
          <img
            className="instagramPreview-footer-1 w-100"
            src={instagramFooter1}
            alt=""
          />
        </Row>
        {!compact ? (
          <>
            <Row className="instagramPreview-description">
              <Col xs="12" className="p-0 px-2">
                {text?.trim() ? (
                  <div className="line-break-anywhere pre-wrap">
                    <strong>{displayName} </strong>
                    {text}
                  </div>
                ) : (
                  <img
                    src={fakeTextBig}
                    alt=""
                    className="instagramPreview-footer"
                  />
                )}
              </Col>
            </Row>
            <Row>
              <img
                className="instagramPreview-footer"
                src={instagramFooter2}
                alt=""
              />
            </Row>
          </>
        ) : null}
      </Container>
    </div>
  );
}

function FacebookStaticPreview({
  text,
  imageUrl,
  displayName,
  avatarUrl,
  onImageClick,
  imageAspect,
  compact,
}: Omit<Props, "platform">) {
  return (
    <div
      className={`facebookPreview-container neu-out p-2${
        compact ? " staticSocialPreview--compact" : ""
      }`}
    >
      <Container fluid>
        <Row className="facebookPreview-header">
          <Col xs="6" className="d-flex align-items-center p-0">
            <Avatar
              url={avatarUrl}
              name={displayName}
              className="facebookPreview-header-avatar"
            />
            <div>
              <div>{displayName}</div>
              <div>
                <img src={fakeTextHeader} alt="" />
              </div>
            </div>
          </Col>
          <Col
            xs="6"
            className="align-content-center col-6 d-flex justify-content-end"
          >
            <FontAwesomeIcon icon={["fas", "ellipsis-h"]} />
          </Col>
        </Row>
        {!compact ? (
          <Row className="facebookPreview-description">
            <Col xs="12" className="p-0 px-2">
              <Caption text={text} emptySrc={fakeText} />
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col xs="12" className="p-0">
            <Media
              imageUrl={imageUrl}
              className="facebookPreview-image"
              onImageClick={onImageClick}
              imageAspect={imageAspect}
            />
          </Col>
        </Row>
        <Row>
          <img
            className="facebookPreview-footer facebookPreview-footer-image"
            src={facebookFooter}
            alt=""
          />
        </Row>
      </Container>
    </div>
  );
}

function LinkedInStaticPreview({
  text,
  imageUrl,
  displayName,
  avatarUrl,
  onImageClick,
  imageAspect,
  compact,
}: Omit<Props, "platform">) {
  return (
    <div
      className={`linkedInPreview-container neu-out p-2${
        compact ? " staticSocialPreview--compact" : ""
      }`}
    >
      <Container fluid>
        <Row className="linkedInPreview-header">
          <Col xs="6" className="d-flex align-items-center p-0">
            <Avatar
              url={avatarUrl}
              name={displayName}
              className="linkedInPreview-header-avatar"
            />
            <div>
              <div className="linkedInPreview-header-subText">{displayName}</div>
              <div>
                <img
                  src={fakeTextHeader}
                  alt=""
                  className="linkedInPreview-header-subText"
                />
                <img src={fakeTextHeader} alt="" />
              </div>
            </div>
          </Col>
          <Col
            xs="6"
            className="align-content-center col-6 d-flex justify-content-end"
          >
            <FontAwesomeIcon icon={["fas", "ellipsis-h"]} />
          </Col>
        </Row>
        {!compact ? (
          <Row className="linkedInPreview-description">
            <Col xs="12" className="line-break-anywhere p-0 pre-wrap">
              <Caption text={text} emptySrc={fakeText} />
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col xs="12" className="p-0">
            <Media
              imageUrl={imageUrl}
              className="linkedInPreview-image"
              onImageClick={onImageClick}
              imageAspect={imageAspect}
            />
          </Col>
        </Row>
        <Row>
          <img
            className="linkedInPreview-footer"
            src={linkedInFooter}
            alt=""
          />
        </Row>
      </Container>
    </div>
  );
}

function TwitterStaticPreview({
  text,
  imageUrl,
  displayName,
  avatarUrl,
  onImageClick,
  imageAspect,
  compact,
}: Omit<Props, "platform">) {
  return (
    <div
      className={`twitterPreview-container neu-out p-2${
        compact ? " staticSocialPreview--compact" : ""
      }`}
    >
      <Container fluid>
        <Row className="twitterPreview-header">
          <Col xs="2">
            <Avatar
              url={avatarUrl}
              name={displayName}
              className="twitterPreview-header-avatar"
            />
          </Col>
          <Col xs="10" className="twitterPreview-header-description">
            <div className="align-items-end d-flex">
              <span className="twitterPreview-header-name">{displayName}</span>
              <img className="ml-3 w-100" src={twitterHeader} alt="" />
            </div>
            {!compact ? (
              <div className="line-break-anywhere pr-4 pre-wrap">
                <Caption text={text} emptySrc={fakeTextBig} />
              </div>
            ) : null}
          </Col>
        </Row>
        <Row>
          <Col xs="12" className="twitterPreview-image-container">
            <Media
              imageUrl={imageUrl}
              className="twitterPreview-image"
              onImageClick={onImageClick}
              imageAspect={imageAspect}
            />
            <img
              className="twitterPreview-footer"
              src={twitterFooter}
              alt=""
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
}

function TiktokStaticPreview({
  text,
  imageUrl,
  displayName,
  avatarUrl,
  onImageClick,
  imageAspect,
  compact,
}: Omit<Props, "platform">) {
  const handle = `@${(displayName || "tu-negocio").replace(/\s+/g, "").toLowerCase()}`;
  const caption = text?.trim()
    ? text.length > 80
      ? `${text.slice(0, 80)}…`
      : text
    : "";

  return (
    <div
      className={`tiktokPreview-container neu-out staticSocialPreview-tiktok${
        compact ? " staticSocialPreview--compact" : ""
      }`}
    >
      <Container fluid className="position-relative tiktok-preview-wrapper">
        <div className="tiktok-preview-header my-3 d-flex">
          <FontAwesomeIcon
            size="1x"
            icon={["fas", "tv"]}
            className="text-white font-bold my-auto mx-4"
          />
          <div className="container justify-content-between d-flex">
            <span style={{ fontSize: "10px" }} className="text-light font-bold">
              Explorar
            </span>
            <span style={{ fontSize: "10px" }} className="text-light font-bold">
              Siguiendo
            </span>
            <span style={{ fontSize: "10px" }} className="text-light font-bold">
              Para ti
            </span>
          </div>
          <FontAwesomeIcon
            size="1x"
            icon={["fas", "search"]}
            className="text-white font-bold my-auto mx-4"
          />
        </div>
        <Row className="tiktok-preview-content">
          <Col xs="12" className="p-0">
            <Media
              imageUrl={imageUrl}
              className="tiktok-preview-media"
              emptyLabel="Diseño"
              onImageClick={onImageClick}
              imageAspect={imageAspect}
            />
          </Col>
        </Row>
        <div className="position-absolute bottom-0 w-100 mx-auto staticSocialPreview-tiktokCaption">
          <div className="d-flex align-items-center px-2 mb-1">
            <Avatar
              url={avatarUrl}
              name={displayName}
              className="staticSocialPreview-tiktokAvatar"
            />
            <p style={{ fontSize: "12px" }} className="font-bold mb-0 text-white">
              {handle}
            </p>
          </div>
          {!compact && caption ? (
            <p style={{ fontSize: "10px" }} className="px-2 mt-1 text-white mb-2">
              {caption}
            </p>
          ) : null}
        </div>
        <div className="tiktok-preview-sidebar">
          <div className="action-column">
            <div className="action-icon">♥</div>
            <div className="action-icon">◌</div>
            <div className="action-icon">↗</div>
          </div>
        </div>
      </Container>
    </div>
  );
}

export function StaticSocialPostPreview(props: Props) {
  const { platform, ...rest } = props;
  switch (platform) {
    case "instagram":
      return <InstagramStaticPreview {...rest} />;
    case "facebook":
      return <FacebookStaticPreview {...rest} />;
    case "linkedin":
      return <LinkedInStaticPreview {...rest} />;
    case "twitter":
      return <TwitterStaticPreview {...rest} />;
    case "tiktok":
      return <TiktokStaticPreview {...rest} />;
    default:
      return <InstagramStaticPreview {...rest} />;
  }
}
